import React, { useEffect, useState, useMemo } from "react";
import { Container, Card } from "react-bootstrap";
import ToastMessage from "../../ToastMessage";
import { WithApiAccess } from "../../../lib/HigherOrderComponents/WithApiAccess";
import { withHeaderMessages } from "../../../higherOrderComponents/WithHeaderMessages";
import { LoadingSpinner } from "../../../lib/components/LoadingSpinner";
import { Currency } from "../../../lib/components/Currency";
import "../Commissions.css";
import { SearchForm } from "./SearchForm";
import { AgentGroupCard } from "./AgentGroupCard";
import { BulkActionBar } from "./BulkActionBar";
import { groupByAgent } from "./helpers";
import { useContractSearch } from "../hooks/useContractSearch";
import { useStatusUpdate } from "./hooks/useStatusUpdate";
import { useDispositionUpdate } from "./hooks/useDispositionUpdate";
import { useContractSelection } from "./hooks/useContractSelection";
import { CommissionStatus } from "./constants";
import {
  createInitialToastState,
  createToastState,
  hideToastState,
} from "../Approvals/utils/toastHelpers";

const ContractCommissionsApproval = (props) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // `contracts` is the one piece of state genuinely shared across concerns:
  // search writes results into it, status updates patch it optimistically, and
  // selection reads from it. So the container owns it and hands it down.
  const [contracts, setContracts] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [toast, setToast] = useState(createInitialToastState);

  const showToast = (message) => {
    setToast(createToastState(message, "danger", "error"));
  };

  const hideToast = () => {
    setToast(hideToastState);
  };

  const { updateStatus, busy, errored, clearErrors } = useStatusUpdate({
    api: props.api,
    setContracts,
    onError: () => showToast("Unable to update contract status."),
  });

  const { updateDisposition, savingDispositions } = useDispositionUpdate({
    api: props.api,
    setContracts,
    onError: () => showToast("Unable to update contract disposition."),
  });

  // A fresh fetch clears prior per-row failures.
  const { isLoading, params, searchAndSetHistory } = useContractSearch({
    api: props.api,
    setContracts,
    onResults: clearErrors,
    onError: () => showToast("Unable to load contracts."),
  });

  const {
    selected,
    setSelected,
    confirmDecline,
    setConfirmDecline,
    activeSelection,
    selCount,
    selAmount,
    approvable,
    approveCount,
    blockedCount,
    approveAmount,
    blockedAmount,
    toggleOne,
    toggleGroup,
    clearSelection,
  } = useContractSelection(contracts);

  const toggleCollapse = (key) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const bulkApprove = async () => {
    setIsSaving(true);
    try {
      const numbers = approvable.map((c) => c.number);
      await updateStatus(numbers, "Approved");
      // Keep any unassigned (un-approvable) selections so the bar shifts to "Decline N".
      setSelected((prev) => {
        const next = new Set(prev);
        numbers.forEach((id) => next.delete(id));
        return next;
      });
    } finally {
      setIsSaving(false);
    }
  };

  const bulkDecline = async () => {
    setIsSaving(true);
    try {
      await updateStatus(
        activeSelection.map((c) => c.number),
        "Declined",
      );
      clearSelection();
    } finally {
      setIsSaving(false);
    }
  };

  const groups = useMemo(() => groupByAgent(contracts), [contracts]);
  const allPending = contracts.filter(
    (c) => c.commissionStatus === CommissionStatus.Pending,
  );

  return (
    <div className="commissions-scope min-vh-100 text-body">
      <ToastMessage
        message={toast.message}
        show={toast.show}
        handleToast={hideToast}
        type={toast.type}
        action={toast.action}
      />
      <Container
        className="py-2 py-sm-3"
        style={{ paddingBottom: selCount > 0 ? "7rem" : undefined }}
      >
        {/* Header */}
        <header className="mb-4">
          <p className="text-secondary mb-0">
            {allPending.length} pending ·{" "}
            <span className="tnum">
              <Currency
                amount={allPending.reduce((s, c) => s + c.netPrice, 0)}
              />
            </span>{" "}
            awaiting review
          </p>
        </header>

        <SearchForm params={params} onSearch={searchAndSetHistory} />

        {/* Results */}
        {isLoading ? (
          <LoadingSpinner />
        ) : groups.length === 0 ? (
          <Card className="border-dashed text-center">
            <Card.Body className="py-5">
              <p className="fw-medium mb-1">No contracts match this search</p>
              <p className="text-secondary mb-0">
                Try a different search term, status, or date range.
              </p>
            </Card.Body>
          </Card>
        ) : (
          <div className="d-flex flex-column gap-3">
            {groups.map(([key, rows]) => (
              <AgentGroupCard
                key={key}
                groupKey={key}
                rows={rows}
                selected={selected}
                onToggleOne={toggleOne}
                onToggleGroup={toggleGroup}
                isCollapsed={collapsed.has(key)}
                onToggleCollapse={() => toggleCollapse(key)}
                busy={busy}
                errored={errored}
                onUpdateStatus={updateStatus}
                onUpdateDisposition={updateDisposition}
                savingDispositions={savingDispositions}
              />
            ))}
          </div>
        )}
      </Container>

      {/* Sticky bulk-action bar */}
      {selCount > 0 && (
        <BulkActionBar
          selCount={selCount}
          selAmount={selAmount}
          blockedCount={blockedCount}
          approveCount={approveCount}
          approveAmount={approveAmount}
          blockedAmount={blockedAmount}
          confirmDecline={confirmDecline}
          isSaving={isSaving}
          onRequestDecline={() => setConfirmDecline(true)}
          onCancelDecline={() => setConfirmDecline(false)}
          onConfirmDecline={bulkDecline}
          onClear={clearSelection}
          onApprove={bulkApprove}
        />
      )}
    </div>
  );
};

export default withHeaderMessages(WithApiAccess(ContractCommissionsApproval));
